import { icon } from '../../icons';
import { type BillingOverviewDto, type BillingSubscriptionDto } from '../../services/billing.api';
import { t } from '../../services/i18n';
import {
  BILLING_PLAN_DETAILS,
  esc,
  formatApproxDocumentCount,
  formatBytes,
  formatCents,
  formatDate,
  formatMoney,
  formatPropertyLimit,
  getTrialDaysRemaining,
  intervalLabel,
  isTrialSubscription,
  renderSubscriptionField,
  subscriptionStatusLabel,
} from './profile-shared';

export class RcProfileTabSubscription extends HTMLElement {
  billingOverview: BillingOverviewDto | null = null;
  isBillingSyncing = false;
  isLoading = false;
  isPortalLoading = false;
  checkoutLoadingKey = '';
  subscriptionLoadingKey = '';
  isCancellingSubscription = false;
  billingError = '';
  billingTermsError = '';
  billingTermsAccepted = false;
  isAdmin = false;
  canManageAccount = true;

  render(): void {
    const planActionInFlight = this.isBillingSyncing || !!this.checkoutLoadingKey || !!this.subscriptionLoadingKey;
    const actionsDisabled = planActionInFlight || this.isPortalLoading || this.isCancellingSubscription;

    if (this.isLoading) {
      this.innerHTML = `<div class="card profile-card"><div class="empty-state">${t('billing.loading')}</div></div>`;
      return;
    }

    if (this.billingError) {
      this.innerHTML = `<div class="card profile-card"><div class="form-error">${esc(this.billingError)}</div></div>`;
      return;
    }

    if (this.isAdmin) {
      this.innerHTML = `
        <div class="card profile-card billing-card billing-subscription-card">
          <div class="billing-admin-panel">
            <div class="billing-current-header">
              <div class="billing-icon-active">
                ${icon('shield-check', { size: 28, strokeWidth: 1.7 })}
              </div>
              <div>
                <div class="billing-eyebrow">${t('admin.role.admin')}</div>
                <h2>${t('billing.adminTitle')}</h2>
                <p>${t('billing.adminBody')}</p>
              </div>
            </div>
            <span class="billing-status-pill">${t('billing.adminBadge')}</span>
          </div>
        </div>
      `;
      return;
    }

    const subscription = this.billingOverview?.subscription;
    const isTrial = isTrialSubscription(subscription, this.billingOverview);
    const hasVisibleSubscription = !!subscription && (
      isTrial ||
      !!subscription.planKey ||
      !!subscription.planName ||
      !!subscription.currentPeriodEndUtc ||
      subscription.hasManageableSubscription ||
      (!!subscription.status && !!subscription.providerSubscriptionId)
    );

    if (!subscription || !hasVisibleSubscription) {
      this.innerHTML = `
        <div class="card profile-card billing-card billing-subscription-card">
          ${this.renderBillingProcessingPanel()}
          <div class="billing-empty-panel">
            <div class="billing-icon-muted">
              ${icon('credit-card', { size: 28, strokeWidth: 1.7 })}
            </div>
            <div>
              <h2>${t('billing.noSubscriptionTitle')}</h2>
              <p>${t('billing.noSubscription')}</p>
            </div>
          </div>
          ${this.canManageAccount ? this.renderPlanPickerSection(undefined, undefined, false, t('billing.choosePlan')) : this.renderReadOnlyNotice()}
        </div>
      `;
      this.attachListeners();
      return;
    }

    if (isTrial) {
      this.innerHTML = `
        <div class="card profile-card billing-card billing-subscription-card">
          ${this.renderBillingProcessingPanel()}
          ${this.renderTrialSummary(subscription)}
          ${this.canManageAccount ? this.renderPlanPickerSection(subscription.planKey, subscription.interval, false, t('billing.choosePlan')) : this.renderReadOnlyNotice()}
        </div>
      `;
      this.attachListeners();
      return;
    }

    const managementHelp = subscription.supportsCustomerPortal
      ? t('billing.manageHelp')
      : t('billing.manageInAppHelp');
    const canManageInApp = subscription.supportsSubscriptionChanges && subscription.hasManageableSubscription;
    const showPlanChoices = subscription.supportsSubscriptionChanges || !subscription.hasManageableSubscription;

    this.innerHTML = `
      <div class="card profile-card billing-card billing-subscription-card">
        ${this.renderBillingProcessingPanel()}
        <div class="billing-current-panel">
          <div class="billing-current-header">
            <div class="billing-icon-active">
              ${icon('credit-card', { size: 28, strokeWidth: 1.7 })}
            </div>
            <div>
              <div class="billing-eyebrow">${t('billing.currentPlan')}</div>
              <h2>${esc(subscription.planName || subscription.planKey || '-')}</h2>
              <p>${managementHelp}</p>
            </div>
          </div>
          <span class="billing-status-pill">${subscriptionStatusLabel(subscription.status)}</span>
        </div>
        <div class="billing-summary-grid">
          ${renderSubscriptionField(t('billing.intervalLabel'), intervalLabel(subscription.interval))}
          ${renderSubscriptionField(
            subscription.cancelAtPeriodEnd ? t('billing.endsOn') : t('billing.renewsOn'),
            formatDate(subscription.currentPeriodEndUtc),
          )}
          ${subscription.creditBalanceInCents > 0
            ? renderSubscriptionField(t('billing.creditBalance'), formatCents(subscription.creditBalanceInCents))
            : ''}
          ${this.renderStorageSummaryFields()}
        </div>
        ${this.renderPendingChange(subscription)}
        ${subscription.cancelAtPeriodEnd ? `<div class="billing-warning">${t('billing.cancelScheduled')}</div>` : ''}
        <div class="profile-actions">
          ${this.canManageAccount && subscription.supportsCustomerPortal ? `
            <button type="button" class="btn btn-primary${actionsDisabled ? ' is-disabled' : ''}" data-action="open-billing-portal" ${actionsDisabled ? 'disabled' : ''}>
              ${this.isPortalLoading ? t('billing.portalLoading') : t('billing.manageSubscription')}
            </button>
          ` : ''}
          ${this.canManageAccount && subscription.supportsSubscriptionCancellation && subscription.hasManageableSubscription ? `
            <button type="button" class="btn btn-outline${actionsDisabled ? ' is-disabled' : ''}" data-action="cancel-subscription" ${actionsDisabled ? 'disabled' : ''}>
              ${this.isCancellingSubscription ? t('billing.subscriptionActionLoading') : t('billing.cancelSubscription')}
            </button>
            ` : ''}
          <a href="/pricing/" class="btn btn-outline${actionsDisabled ? ' is-disabled' : ''}" ${actionsDisabled ? 'aria-disabled="true" tabindex="-1"' : ''}>${t('billing.comparePlans')}</a>
        </div>
        ${showPlanChoices && this.canManageAccount ? this.renderPlanPickerSection(
          subscription.planKey,
          subscription.interval,
          canManageInApp,
          canManageInApp ? t('billing.changePlan') : t('billing.choosePlan'),
        ) : ''}
        ${!this.canManageAccount ? this.renderReadOnlyNotice() : ''}
      </div>
    `;
    this.attachListeners();
  }

  private renderBillingProcessingPanel(): string {
    if (!this.isBillingSyncing) {
      return '';
    }

    return `
      <div class="billing-processing-panel" role="status" aria-live="polite">
        <div class="billing-processing-main">
          <span class="billing-processing-spinner" aria-hidden="true"></span>
          <div>
            <div class="billing-eyebrow">${t('billing.processingEyebrow')}</div>
            <h2>${t('billing.processingTitle')}</h2>
            <p>${t('billing.processingBody')}</p>
          </div>
        </div>
        <div class="billing-processing-note">${t('billing.processingHint')}</div>
      </div>
    `;
  }

  private attachListeners(): void {
    this.querySelector<HTMLInputElement>('[data-billing-terms-consent]')?.addEventListener('change', (event) => {
      this.dispatchEvent(new CustomEvent('profile-billing-terms-consent', {
        bubbles: true,
        detail: { accepted: (event.currentTarget as HTMLInputElement).checked },
      }));
    });

    this.querySelector<HTMLButtonElement>('[data-action="open-billing-portal"]')?.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('profile-open-billing-portal', { bubbles: true }));
    });

    this.querySelector<HTMLButtonElement>('[data-action="cancel-subscription"]')?.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('profile-cancel-subscription', { bubbles: true }));
    });

    this.querySelectorAll<HTMLButtonElement>('[data-action="start-checkout"]').forEach((button) => {
      button.addEventListener('click', () => this.dispatchPlanAction('profile-start-checkout', button));
    });

    this.querySelectorAll<HTMLButtonElement>('[data-action="update-subscription"]').forEach((button) => {
      button.addEventListener('click', () => this.dispatchPlanAction('profile-update-subscription', button));
    });
  }

  private dispatchPlanAction(eventName: string, button: HTMLButtonElement): void {
    const planKey = button.dataset.planKey;
    const billingInterval = button.dataset.billingInterval;
    if (!planKey || (billingInterval !== 'monthly' && billingInterval !== 'yearly')) {
      return;
    }

    this.dispatchEvent(new CustomEvent(eventName, {
      bubbles: true,
      detail: { planKey, billingInterval },
    }));
  }

  private renderAvailablePlans(
    currentPlanKey?: string | null,
    currentInterval?: string | null,
    useSubscriptionChange = false,
  ): string {
    const plans = this.billingOverview?.plans ?? [];
    if (!plans.length) {
      return '';
    }

    const planActionInFlight = this.isBillingSyncing || !!this.checkoutLoadingKey || !!this.subscriptionLoadingKey;
    const newSubscriptionAccessEnabled = this.billingOverview?.newSubscriptionAccessEnabled !== false;
    const canStartNewSubscription = this.billingOverview?.canStartNewSubscription !== false;

    return `
      <div class="billing-plan-list">
        ${plans.map((plan) => {
          const details = BILLING_PLAN_DETAILS[plan.key];
          const isCurrent = currentPlanKey === plan.key;
          const isCurrentMonthly = isCurrent && currentInterval === 'monthly';
          const isCurrentYearly = isCurrent && currentInterval === 'yearly';
          const action = useSubscriptionChange ? 'update-subscription' : 'start-checkout';
          const disabledByNewSubscriptionAccess = !useSubscriptionChange && (!newSubscriptionAccessEnabled || !canStartNewSubscription);
          const monthlyDisabled = planActionInFlight || isCurrentMonthly || disabledByNewSubscriptionAccess;
          const yearlyDisabled = planActionInFlight || isCurrentYearly || disabledByNewSubscriptionAccess;

          return `
            <article class="billing-plan-card${isCurrent ? ' is-current' : ''}${details?.featured ? ' is-featured' : ''}">
              ${details?.featured ? `<div class="billing-plan-popular">${t('pricing.badge.popular')}</div>` : ''}
              <div class="billing-plan-card-head">
                <div>
                  <strong>${esc(plan.displayName)}</strong>
                  ${details ? `<p class="billing-plan-desc">${t(details.descriptionKey)}</p>` : `<div class="billing-invoice-meta">${formatPropertyLimit(plan.propertyLimit)}</div>`}
                </div>
              </div>
              ${details ? `
                <div class="billing-plan-prices">
                  <div>
                    <span class="billing-plan-price">${this.formatPlanPrice(plan, 'monthly')}</span>
                    <span class="billing-plan-period">${t('pricing.period.monthly')}</span>
                  </div>
                  <div>
                    <span class="billing-plan-price">${this.formatPlanPrice(plan, 'yearly')}</span>
                    <span class="billing-plan-period">${t('pricing.period.yearly')}</span>
                    <span class="billing-save-badge">${t('pricing.toggle.saveLabel')}</span>
                  </div>
                </div>
                <ul class="billing-plan-features">
                  <li>${icon('home', { size: 15, strokeWidth: 2.5 })}<span>${formatPropertyLimit(plan.propertyLimit)}</span></li>
                  <li>${icon('file', { size: 15, strokeWidth: 2.5 })}<span>${t('billing.storagePlanFeature', { storage: formatBytes(plan.storageLimitBytes), count: formatApproxDocumentCount(plan.storageLimitBytes) })}</span></li>
                  ${details.features.map((featureKey) => `<li>${icon('check', { size: 15, strokeWidth: 2.5 })}<span>${t(featureKey)}</span></li>`).join('')}
                </ul>
              ` : ''}
              <div class="profile-actions">
                <button
                  type="button"
                  class="btn btn-primary${monthlyDisabled ? ' is-disabled' : ''}"
                  data-action="${action}"
                  data-plan-key="${esc(plan.key)}"
                  data-billing-interval="monthly"
                  data-requires-billing-consent="true"
                  data-disabled-by-state="${monthlyDisabled ? 'true' : 'false'}"
                  ${monthlyDisabled ? 'disabled' : ''}>
                  ${this.checkoutLoadingKey === `${plan.key}:monthly` || this.subscriptionLoadingKey === `${plan.key}:monthly`
                    ? t('billing.subscriptionActionLoading')
                    : isCurrentMonthly
                      ? t('billing.currentSelection')
                      : t('pricing.toggle.monthly')}
                </button>
                <button
                  type="button"
                  class="btn btn-outline${yearlyDisabled ? ' is-disabled' : ''}"
                  data-action="${action}"
                  data-plan-key="${esc(plan.key)}"
                  data-billing-interval="yearly"
                  data-requires-billing-consent="true"
                  data-disabled-by-state="${yearlyDisabled ? 'true' : 'false'}"
                  ${yearlyDisabled ? 'disabled' : ''}>
                  ${this.checkoutLoadingKey === `${plan.key}:yearly` || this.subscriptionLoadingKey === `${plan.key}:yearly`
                    ? t('billing.subscriptionActionLoading')
                    : isCurrentYearly
                      ? t('billing.currentSelection')
                      : t('pricing.toggle.yearly')}
                </button>
              </div>
            </article>
          `;
        }).join('')}
      </div>
    `;
  }

  private renderPlanPickerSection(
    currentPlanKey?: string | null,
    currentInterval?: string | null,
    useSubscriptionChange = false,
    title = '',
  ): string {
    return `
      <section class="billing-plan-picker">
        <div class="billing-section-head">
          <h2>${esc(title)}</h2>
        </div>
        ${!useSubscriptionChange && this.billingOverview?.newSubscriptionAccessEnabled === false ? `
          <div class="billing-warning">
            ${t('billing.newSubscriptionsDisabled')}
          </div>
        ` : ''}
        ${!useSubscriptionChange && this.billingOverview?.canStartNewSubscription === false ? `
          <div class="billing-warning">
            ${t('billing.userNewSubscriptionsDisabled')}
          </div>
        ` : ''}
        ${this.renderSubscriptionTerms()}
        <label class="billing-terms-consent">
          <input type="checkbox" data-billing-terms-consent ${this.billingTermsAccepted ? 'checked' : ''}>
          <span>${t('billing.changeTerms.consentLabel')}</span>
        </label>
        ${this.billingTermsError ? `
          <div class="billing-terms-error alert alert-warning" role="alert">
            <strong>${esc(this.billingTermsError)}</strong>
          </div>
        ` : ''}
        ${this.renderAvailablePlans(currentPlanKey, currentInterval, useSubscriptionChange)}
      </section>
    `;
  }

  private renderPendingChange(subscription: BillingSubscriptionDto): string {
    if (!subscription.pendingPlanKey) {
      return '';
    }

    const targetPlan = subscription.pendingPlanName || subscription.pendingPlanKey;
    const targetInterval = intervalLabel(subscription.pendingInterval);
    const effectiveAt = formatDate(subscription.pendingEffectiveAtUtc);

    return `
      <div class="billing-warning">
        <strong>${t('billing.pendingChangeTitle')}</strong><br>
        ${this.renderPendingChangeBody(targetPlan, targetInterval, effectiveAt, subscription.pendingEffectiveAtUtc)}
      </div>
    `;
  }

  private renderPendingChangeBody(
    targetPlan: string,
    targetInterval: string,
    effectiveAt: string,
    pendingEffectiveAtUtc?: string | null,
  ): string {
    const effectiveDate = pendingEffectiveAtUtc ? new Date(pendingEffectiveAtUtc) : null;
    const isImmediatePending = !!effectiveDate && !Number.isNaN(effectiveDate.getTime()) &&
      effectiveDate.getTime() <= Date.now() + (60 * 60 * 1000);

    return isImmediatePending
      ? t('billing.pendingChangeBodyAfterPayment', { plan: targetPlan, interval: targetInterval })
      : t('billing.pendingChangeBody', { plan: targetPlan, interval: targetInterval, date: effectiveAt });
  }

  private renderSubscriptionTerms(): string {
    return `
      <details class="billing-terms">
        <summary>
          <span class="billing-terms-marker">*</span>
          <span>${t('billing.changeTerms.linkLabel')}</span>
          <span class="billing-terms-caret" aria-hidden="true">▾</span>
        </summary>
        <div class="billing-terms-body">
          <p>${t('billing.changeTerms.intro')}</p>
          <p>${t('billing.changeTerms.downgrade')}</p>
          <p>${t('billing.changeTerms.upgrade')}</p>
        </div>
      </details>
    `;
  }

  private renderTrialSummary(subscription: BillingSubscriptionDto): string {
    const daysRemaining = getTrialDaysRemaining(this.billingOverview);
    const daysLabel = daysRemaining === 1
      ? t('billing.trialDayRemaining')
      : t('billing.trialDaysRemaining', { count: daysRemaining });
    const planName = subscription.planName || subscription.planKey || t('pricing.trial.title');

    return `
      <div class="billing-trial-panel">
        <div class="billing-trial-main">
          <div class="billing-icon-active">
            ${icon('sparkles', { size: 28, strokeWidth: 1.7 })}
          </div>
          <div>
            <div class="billing-eyebrow">${t('billing.status.trialing')}</div>
            <h2>${t('billing.trialTitle')}</h2>
            <p>${t('billing.trialBody')}</p>
          </div>
        </div>
        <div class="billing-trial-days">
          <strong>${daysRemaining}</strong>
          <span>${daysLabel}</span>
        </div>
      </div>
      <div class="billing-summary-grid">
        ${renderSubscriptionField(t('billing.planLabel'), planName)}
        ${renderSubscriptionField(t('billing.statusLabel'), subscriptionStatusLabel(subscription.status))}
        ${renderSubscriptionField(t('billing.trialStartedOn'), formatDate(this.billingOverview?.trialStartedAtUtc))}
        ${renderSubscriptionField(t('billing.trialEndsOn'), formatDate(this.billingOverview?.trialEndsAtUtc))}
        ${this.renderStorageSummaryFields()}
      </div>
    `;
  }

  private renderStorageSummaryFields(): string {
    const usage = this.billingOverview?.storageUsage;
    if (!usage) {
      return '';
    }

    return [
      renderSubscriptionField(t('billing.storageUsed'), formatBytes(usage.usedBytes)),
      renderSubscriptionField(t('billing.storageAvailable'), formatBytes(usage.limitBytes)),
      renderSubscriptionField(t('billing.storageRemaining'), formatBytes(usage.remainingBytes)),
    ].join('');
  }

  private renderReadOnlyNotice(): string {
    return `<div class="billing-warning">${t('profile.accountMembers.readerBillingNotice')}</div>`;
  }

  private formatPlanPrice(plan: { monthlyPriceInCents?: number; yearlyPriceInCents?: number; currency?: string }, interval: 'monthly' | 'yearly'): string {
    const amount = interval === 'yearly' ? plan.yearlyPriceInCents : plan.monthlyPriceInCents;
    return typeof amount === 'number' ? formatMoney(amount, plan.currency || 'EUR') : formatCents(0);
  }
}

if (!customElements.get('rc-profile-tab-subscription')) {
  customElements.define('rc-profile-tab-subscription', RcProfileTabSubscription);
}
